/**
 * QuizPreviewModal
 * Modal para revisar el quiz generado antes de guardarlo.
 * Proporciona espacio suficiente para ver todas las preguntas y opciones.
 */

import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@learn-me/ui";
import {
  CheckCircle,
  X,
  Save,
  Trash2,
  HelpCircle,
  Loader2,
} from "lucide-react";
import type { GeneratedQuiz } from "../api/adminApi";

interface QuizPreviewModalProps {
  quiz: GeneratedQuiz;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
}

export function QuizPreviewModal({
  quiz,
  open,
  onOpenChange,
  onSave,
  onDiscard,
  isSaving,
}: QuizPreviewModalProps) {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isSaving],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleKeyDown]);

  const handleSave = () => {
    onSave();
    // Modal will close when parent resets generatedQuiz state
  };

  const handleDiscard = () => {
    onDiscard();
    onOpenChange(false);
  };

  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !isSaving && onOpenChange(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] mx-4 bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <HelpCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-green-800">Quiz Generado</h2>
              <p className="text-sm text-green-600">{quiz.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full font-medium">
              {quiz.questions.length} preguntas
            </span>
            <button
              onClick={() => !isSaving && onOpenChange(false)}
              disabled={isSaving}
              className="p-1.5 hover:bg-green-100 rounded-lg transition-colors text-green-600 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-sm text-gray-600 mb-4">
            Revisa las preguntas generadas antes de guardar el quiz.
          </p>

          {/* Questions List */}
          <div className="space-y-4">
            {quiz.questions.map((question, qIdx) => (
              <div
                key={qIdx}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 text-green-700 text-sm font-semibold flex items-center justify-center">
                    {qIdx + 1}
                  </span>
                  <div className="flex-1 space-y-3">
                    <p className="font-medium text-gray-900">{question.text}</p>

                    {/* Options */}
                    <div className="space-y-2">
                      {question.options.map((option, oIdx) => (
                        <div
                          key={oIdx}
                          className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${
                            option.isCorrect
                              ? "bg-green-100 text-green-800 border border-green-300"
                              : "bg-white text-gray-700 border border-gray-200"
                          }`}
                        >
                          {option.isCorrect ? (
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                          )}
                          <span className="flex-1">{option.text}</span>
                          {option.isCorrect && (
                            <span className="text-xs font-semibold text-green-600 bg-green-200 px-2 py-0.5 rounded">
                              Correcta
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Explanation */}
                    {question.explanation && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-gray-700">
                            Explicación:{" "}
                          </span>
                          {question.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Quiz
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDiscard}
              disabled={isSaving}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Descartar
            </Button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            Al guardar, este quiz se asociará al capítulo actual.
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
